import {
	type ArcadeView,
	arcadeWinner,
	type FlitState,
	getArcade,
	type InputEvent,
	type NibbleState,
	type PindropState,
	type PlayerIndex,
	type RealtimeState,
	type SorterState,
	type TumbleState,
	type ZoomState,
} from "@playpal/core";
import { useEffect, useRef, useState } from "react";
import { FLIT_ASPECT, FlitControls, FlitField } from "../games/FlitBoard";
import { NIBBLE_ASPECT, NibbleControls, NibbleField } from "../games/NibbleBoard";
import { PINDROP_ASPECT, PindropControls, PindropField } from "../games/PindropBoard";
import { SORTER_ASPECT, SorterControls, SorterField } from "../games/SorterBoard";
import { TUMBLE_ASPECT, TumbleControls, TumbleField } from "../games/TumbleBoard";
import { ZOOM_ASPECT, ZoomControls, ZoomField } from "../games/ZoomBoard";
import { play } from "../lib/sound";
import { useRealtimeRun } from "../lib/useRealtimeRun";
import { ArcadeStage } from "./ArcadeStage";
import { Button, Card } from "./ui";

/** How many times a finished run is offered up before a child is told. */
const SEND_ATTEMPTS = 4;
/** Long enough for a reply on a slow connection, short enough not to be a wait. */
const RETRY_MS = 2000;

interface Props {
	view: ArcadeView;
	seat: PlayerIndex;
	friendName: string;
	/** Whether there is a connection to send anything on. */
	connected: boolean;
	/**
	 * Sends the list of presses up, tagged with the seed of the race they were
	 * played in. The score comes back from the server; the seed is how the
	 * sender knows a kept log has gone stale and must not be re-sent.
	 */
	onSubmit: (log: readonly InputEvent<unknown>[], seed: number) => void;
	/** Called once the score has come back, so nothing keeps re-sending it. */
	onLanded: () => void;
	/** Starts a fresh race — a new seed for both of them. */
	onAgain: () => void;
	onLobby: () => void;
}

/**
 * One arcade race: the same seed on both devices, two runs, one result.
 *
 * The child plays alone here and that is the point. Two snakes on one grid over
 * a home broadband connection is a latency problem wearing a game as a
 * disguise; racing the same board is what children do with these anyway, and it
 * survives a train going into a tunnel.
 */
export function ArcadeRace({
	view,
	seat,
	friendName,
	connected,
	onSubmit,
	onLanded,
	onAgain,
	onLobby,
}: Props) {
	const entry = getArcade(view.arcadeId);
	const mine = view.runs[seat];
	const theirs = view.runs[seat === 0 ? 1 : 0];

	// Whether the player has tapped Start. Reset for the next race by the
	// remount — this component is keyed on the seed.
	const [started, setStarted] = useState(false);
	/*
	 * The finished log, held until the server has acknowledged it by sending
	 * back a room with this seat's run in it.
	 *
	 * Sending once and forgetting was a trap: the socket drops a message when
	 * it is not open, so a connection blip at the wrong second threw the log
	 * away — and nothing retried, because the run loop had already stopped and
	 * the effect that drove it never re-runs. A child who had just played three
	 * minutes of Snake sat on "sending your score" for ever, and their friend
	 * sat on "waiting for them to finish" for ever, in the tunnel this design
	 * is supposed to survive.
	 */
	const [pending, setPending] = useState<readonly InputEvent<unknown>[] | null>(null);
	/*
	 * How many times the log has been offered up.
	 *
	 * Sending once and waiting was not enough. A run is only known to have
	 * landed when the child's own score comes back in a room broadcast, and
	 * either half of that — the log going up, the room coming down — can be
	 * lost to a tunnel or a phone going to sleep. One lost message left a child
	 * on "sending your score" with nothing that would ever try again.
	 */
	const [attempt, setAttempt] = useState(0);
	// Paused while the child decides whether to stop. The run does not advance,
	// which is free: a paused game banks nothing, and tab-switching already
	// works this way.
	const [paused, setPaused] = useState(false);

	const running = started && !mine;

	// Not while paused, and not once there is a log waiting to go up: giving up
	// stops the game, and a run that carried on afterwards would keep scoring
	// against a log already on its way to the server.
	const run = useRealtimeRun(entry.game, view.seed, running && !paused && !pending, (log) => {
		// A run ends once, but the frame that noticed can be replayed by React.
		setPending((already) => already ?? (log as readonly InputEvent<unknown>[]));
	});

	/*
	 * Offers the log up, and keeps offering until the room comes back with this
	 * seat's score in it — which is the only acknowledgement there is.
	 *
	 * Asking twice is safe by design: the server treats a second copy of a run
	 * as a client that missed the answer and replies with the room rather than a
	 * refusal. Bounded, so a genuine refusal does not become a client that asks
	 * for ever, and so a child is told something rather than watching a screen.
	 */
	const givenUp = attempt >= SEND_ATTEMPTS;

	useEffect(() => {
		// `attempt` is read here rather than through `givenUp`, because it is
		// what makes this run again: the effect re-firing on a new attempt is the
		// retry, and a dependency the body does not touch is one a linter is
		// right to call a lie.
		if (!pending || mine || !connected || attempt >= SEND_ATTEMPTS) return;
		onSubmit(pending, view.seed);
		const timer = setTimeout(() => setAttempt((made) => made + 1), RETRY_MS);
		return () => clearTimeout(timer);
	}, [pending, mine, connected, onSubmit, attempt, view.seed]);

	// The score is in, so stop offering the run up.
	useEffect(() => {
		if (mine) onLanded();
	}, [mine, onLanded]);

	useResultChime(view, seat);

	const result = arcadeWinner(view);

	if (result) {
		return (
			<ArcadeResult
				view={view}
				seat={seat}
				friendName={friendName}
				winner={result.winner}
				onAgain={onAgain}
				onLobby={onLobby}
			/>
		);
	}

	if (mine) {
		return (
			<Card className="text-center">
				<p className="text-6xl" aria-hidden="true">
					{entry.emoji}
				</p>
				<h3 className="mt-2 text-3xl font-black">You scored {mine.score}!</h3>
				<p className="mt-3 text-lg font-semibold opacity-80">
					Waiting for {friendName} to finish their go…
				</p>
			</Card>
		);
	}

	if (pending && givenUp) {
		return (
			<Card className="text-center">
				<h3 className="text-3xl font-black">We couldn't send your score</h3>
				<p className="mt-3 text-lg font-semibold opacity-80">
					Your go is safe — it just hasn't got through yet.
				</p>
				<div className="mt-5 flex flex-wrap justify-center gap-3">
					<Button size="lg" onClick={() => setAttempt(0)}>
						🔁 Try again
					</Button>
					<Button size="lg" tone="secondary" onClick={onLobby}>
						🎲 Another game
					</Button>
				</div>
			</Card>
		);
	}

	if (!running) {
		return (
			<Card className="text-center">
				<p className="text-6xl" aria-hidden="true">
					{entry.emoji}
				</p>
				<h3 className="mt-1 text-3xl font-black">{entry.title}</h3>
				<ol className="mx-auto mt-4 max-w-md list-decimal space-y-1 pl-6 text-left text-lg text-indigo-900/80 dark:text-indigo-100/80">
					{entry.howTo.map((line) => (
						<li key={line}>{line}</li>
					))}
				</ol>
				{theirs && (
					<p className="mt-4 text-lg font-bold">
						{friendName} scored {theirs.score}. Beat it!
					</p>
				)}
				<Button className="mt-5" size="lg" onClick={() => setStarted(true)}>
					▶️ Start
				</Button>
			</Card>
		);
	}

	const state = run.state as RealtimeState;
	const live = !run.finished && !paused && !pending;

	return (
		<ArcadeStage
			aspect={ASPECTS[view.arcadeId]}
			score={state.score}
			friendName={friendName}
			friendScore={theirs?.score ?? null}
			paused={paused}
			onPause={() => setPaused(true)}
			onResume={() => setPaused(false)}
			onGiveUp={() => {
				// What they earned before they stopped, and nothing more. The
				// server replays these presses and carries on from the last one,
				// which ends the run much as walking away from it would.
				setPaused(false);
				setPending((already) => already ?? (run.peek().log as readonly InputEvent<unknown>[]));
			}}
			notice={
				pending
					? `${run.over ? "Ouch! " : run.finished ? "Time's up! " : ""}Sending your score…`
					: null
			}
			controls={
				<ArcadeControls
					arcadeId={view.arcadeId}
					state={state}
					onPress={run.press}
					pending={() => run.peek().queue}
					live={live}
				/>
			}
		>
			<ArcadeField
				arcadeId={view.arcadeId}
				state={state}
				onPress={run.press}
				pending={() => run.peek().queue}
				live={live}
			/>
		</ArcadeStage>
	);
}

/** Each world's shape, which the stage scales to fit and never reshapes. */
const ASPECTS: Record<ArcadeView["arcadeId"], number> = {
	nibble: NIBBLE_ASPECT,
	tumble: TUMBLE_ASPECT,
	flit: FLIT_ASPECT,
	sorter: SORTER_ASPECT,
	pindrop: PINDROP_ASPECT,
	zoom: ZOOM_ASPECT,
};

function ArcadeField({
	arcadeId,
	state,
	onPress,
	pending,
	live,
}: {
	arcadeId: ArcadeView["arcadeId"];
	state: RealtimeState;
	// Wider than any one game's input, so it fits all of them: a handler that
	// takes anything is a handler that takes a snake's turns.
	onPress: (input: unknown) => void;
	// Only the snake needs this — a swipe names a direction, and turning that
	// into a left or a right has to account for turns already queued.
	// biome-ignore lint/suspicious/noExplicitAny: one switch, many input shapes
	pending: () => readonly any[];
	live: boolean;
}) {
	switch (arcadeId) {
		case "nibble":
			return (
				<NibbleField state={state as NibbleState} onPress={onPress} pending={pending} live={live} />
			);
		case "tumble":
			return <TumbleField state={state as TumbleState} onPress={onPress} live={live} />;
		case "flit":
			// The only one whose board is also its button.
			return <FlitField state={state as FlitState} onPress={onPress} live={live} />;
		case "sorter":
			return <SorterField state={state as SorterState} onPress={onPress} live={live} />;
		case "pindrop":
			return <PindropField state={state as PindropState} onPress={onPress} live={live} />;
		case "zoom":
			// Swiped and tapped like the flying game, but with three things to
			// say rather than one, so the buttons below stay as well.
			return <ZoomField state={state as ZoomState} onPress={onPress} live={live} />;
	}
}

function ArcadeControls({
	arcadeId,
	state,
	onPress,
	pending,
	live,
}: {
	arcadeId: ArcadeView["arcadeId"];
	state: RealtimeState;
	onPress: (input: unknown) => void;
	// Only the snake needs this — its keys are absolute and its inputs are not.
	// biome-ignore lint/suspicious/noExplicitAny: one switch, many input shapes
	pending: () => readonly any[];
	live: boolean;
}) {
	switch (arcadeId) {
		case "nibble":
			return (
				<NibbleControls
					state={state as NibbleState}
					onPress={onPress}
					pending={pending}
					live={live}
				/>
			);
		case "tumble":
			return <TumbleControls onPress={onPress} live={live} />;
		case "flit":
			return <FlitControls />;
		case "sorter":
			return <SorterControls state={state as SorterState} onPress={onPress} live={live} />;
		case "pindrop":
			return <PindropControls state={state as PindropState} onPress={onPress} live={live} />;
		case "zoom":
			return <ZoomControls onPress={onPress} live={live} />;
	}
}

function ArcadeResult({
	view,
	seat,
	friendName,
	winner,
	onAgain,
	onLobby,
}: {
	view: ArcadeView;
	seat: PlayerIndex;
	friendName: string;
	winner: 0 | 1 | null;
	onAgain: () => void;
	onLobby: () => void;
}) {
	const mine = view.runs[seat];
	const theirs = view.runs[seat === 0 ? 1 : 0];
	const headline =
		winner === null
			? "A dead heat! 🤝"
			: winner === seat
				? "You win! 🎉"
				: `${friendName} wins! 👏`;

	return (
		<Card className="animate-pop text-center">
			<h3 className="text-4xl font-black">{headline}</h3>
			<div className="mt-4 flex items-center justify-center gap-8">
				<Tally label="You" score={mine?.score ?? 0} survived={mine?.survived ?? false} />
				<Tally label={friendName} score={theirs?.score ?? 0} survived={theirs?.survived ?? false} />
			</div>
			{/* Scores tie far more often here than in a turn game — a dozen apples
			    each is an ordinary evening — so say why the tie broke. */}
			{mine && theirs && mine.score === theirs.score && mine.steps !== theirs.steps && (
				<p className="mt-3 text-lg font-semibold opacity-80">
					Same apples, so the longer run took it.
				</p>
			)}
			<div className="mt-5 flex flex-wrap justify-center gap-3">
				<Button size="lg" onClick={onAgain}>
					🔁 Race again
				</Button>
				<Button size="lg" tone="secondary" onClick={onLobby}>
					🎲 Another game
				</Button>
			</div>
		</Card>
	);
}

function Tally({ label, score, survived }: { label: string; score: number; survived: boolean }) {
	return (
		<div>
			<p className="text-lg font-bold">{label}</p>
			<p className="text-4xl font-black">{score}</p>
			{survived && <p className="text-sm font-semibold opacity-70">still alive!</p>}
		</div>
	);
}

/** The same chime the turn games play, once a race has an answer. */
function useResultChime(view: ArcadeView, seat: PlayerIndex) {
	const previous = useRef<string | null>(null);
	useEffect(() => {
		const result = arcadeWinner(view);
		const key = result ? `${view.seed}:${result.winner}` : null;
		if (key && key !== previous.current) play(result?.winner === seat ? "win" : "lose");
		previous.current = key;
	}, [view, seat]);
}
